/**
 * The parent re-check prompt — #578 AD-05, gap G4.
 *
 * WHY IT EXISTS. The server refuses a parent change made with an old parent proof, answering
 * `403 { code: 'PARENT_REAUTH_REQUIRED' }` (`server/auth/parentProof.js`). The owner's rule
 * (2026-09-22): the parent re-proves with a password or Google sign-in, which opens a 5-minute
 * window; a kid PIN never can. Without a prompt, every parent button failed five minutes after
 * sign-in with an error nobody could act on.
 *
 * HOW. This provider registers ONE handler with the API client (`setParentRecheckHandler`), which
 * is the application's only fetch path. The client calls it on that exact 403, and the handler's
 * promise resolves `true` when the parent re-checked (the client then sends the SAME request once
 * more) or `false` when they cancelled (the client throws the refusal to the screen).
 *
 * A Google-only parent has no password to type. Google sign-in is a full-page redirect, so the
 * pending change cannot be resumed: the parent comes back signed in, with a fresh window, and
 * presses the button again. The prompt says so rather than pretending otherwise.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import Button from '../components/Button';
import Field from '../components/Field';
import { apiUrl, setParentRecheckHandler } from '../api/client.js';
import { fetchReauthStatus, reauthWithPassword } from '../api/session.js';

const WRONG = 'That password did not work — try again.';
const FAULT = 'We could not check that just now — please try again in a moment.';

export function ParentRecheckProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState({ password: true, google: false });
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);
  const titleId = useId();

  const settle = useCallback((answer) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    setPassword('');
    setError(null);
    setBusy(false);
    if (resolve) resolve(answer);
  }, []);

  useEffect(() => {
    const release = setParentRecheckHandler(
      () =>
        new Promise((resolve) => {
          // A second refusal while the prompt is already open joins the same answer.
          const previous = resolverRef.current;
          resolverRef.current = previous
            ? (answer) => { previous(answer); resolve(answer); }
            : resolve;
          setOpen(true);
          fetchReauthStatus()
            .then((status) => { if (status?.methods) setMethods(status.methods); })
            .catch(() => { /* keep the password field: the server still decides */ });
        }),
    );
    return () => {
      release();
      // Unmounting with a prompt open is a cancel, never a hang.
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = null;
    };
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!password) return;
    setBusy(true);
    setError(null);
    try {
      const result = await reauthWithPassword(password);
      if (result.outcome === 'ok') {
        settle(true);
        return;
      }
      if (result.outcome === 'use-google') setMethods({ password: false, google: true });
      setError(result.outcome === 'use-google' ? null : WRONG);
    } catch {
      setError(FAULT);
    }
    setBusy(false);
  };

  return (
    <>
      {children}
      {open ? (
        <div className="parent-recheck" role="dialog" aria-modal="true" aria-labelledby={titleId} data-part="parent-recheck">
          <form className="parent-recheck__card" onSubmit={onSubmit}>
            <h2 id={titleId}>Confirm it&rsquo;s you</h2>
            <p>This change is for grown-ups. Please confirm it is you to carry on.</p>
            {methods.password ? (
              <Field
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                error={error || undefined}
                autoFocus
              />
            ) : null}
            {!methods.password && error ? <p role="status">{error}</p> : null}
            {methods.google ? (
              <>
                <p>Signing in with Google takes you away for a moment. Press your button again when you are back.</p>
                <Button type="button" variant="secondary" onClick={() => window.location.assign(apiUrl('/api/auth/google'))}>
                  Continue with Google
                </Button>
              </>
            ) : null}
            <div className="parent-recheck__actions">
              {methods.password ? (
                <Button type="submit" variant="primary" loading={busy} busyLabel="Checking…">
                  Confirm
                </Button>
              ) : null}
              <Button type="button" variant="quiet" onClick={() => settle(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}

export default ParentRecheckProvider;
